import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { DropdownService } from 'src/app/services/dropdown.service'; // Ajusta la ruta
import { FileStorageService } from 'src/app/services/file-storage.service'; // Ajusta la ruta
import { AlertService } from 'src/app/services/alert.service'; // Ajusta la ruta
import { AuthService } from 'src/app/services/auth.service'; // Ajusta la ruta
import {
  AddAddress,
  GetAddress,
  RegisterRequest,
} from 'src/app/models/security/register-request';
import { UserRoleIds } from 'src/app/models/security/user-role-enum';
import {
  DropdownOption,
  DropDownKeywords,
} from 'src/app/models/shared/dropdown-option';
import {
  passwordValidator,
  matchPasswords,
} from 'src/app/validators/password-validation';
import { AddressDialogComponent } from '../address-dialog/address-dialog.component';
import { Router } from '@angular/router';
import { ViewModalEnum } from 'src/app/enums/operation';
import { MatDialog } from '@angular/material/dialog';
import { GetUserCompany } from 'src/app/models/company/get-company';
import { FormErrorsComponent } from 'src/app/components/form-errors/form-errors.component';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { MAX_FILE_SIZE } from 'src/app/constants/file.constants';
import { CountryListDropdownComponent } from 'src/app/app/components/shared/country-list-dropdown/country-list-dropdown.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    FormErrorsComponent,
    NgxMaskDirective,
    CountryListDropdownComponent,
  ],
  selector: 'app-register-form-company',
  templateUrl: './register-form-company.component.html',
  styles: [],
})
export class RegisterFormCompanyComponent implements OnInit, OnChanges {
  /**
   * Valida el tipo de archivo permitido (imágenes y PDF)
   */
  validateFileType(file: File): boolean {
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    return allowed.includes(file.type);
  }

  /**
   * Validación específica para fotos de perfil (solo JPG y PNG)
   */
  validatePhotoFileType(file: File): boolean {
    const allowedPhotoTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    return allowedPhotoTypes.includes(file.type);
  }

  /**
   * Genera una previsualización local del archivo seleccionado
   */
  previewFile(file: File, callback: (preview: string) => void): void {
    const reader = new FileReader();
    reader.onload = () => {
      callback(reader.result as string);
    };
    reader.readAsDataURL(file);
  }

  @Input() showSaveButton: boolean = false;
  @Input() showSaveRegister: boolean = false;

  @Input() isEditMode: boolean = false;
  @Input() isViewMode: boolean = false;
  @Input() companyData: GetUserCompany | null | undefined = null;
  @Input() companyAddresses: GetAddress[] = [];

  @Output() savedEmitter = new EventEmitter<boolean>();

  form!: FormGroup;
  addresses: AddAddress[] = [];
  typeIdentList: DropdownOption[] = [];
  economicActList: DropdownOption[] = [];

  logoPreview: string | null = null;
  repPhotoPreview: string | null = null;
  mercantilePreview: string | null = null;
  legalRepIdPreview: string | null = null;

  // Propiedades para indicadores de carga
  isLogoUploading: boolean = false;
  isRepPhotoUploading: boolean = false;
  isDocUploading: boolean = false;

  // Propiedades para mensajes de error
  logoUploadError: string | null = null;
  repPhotoUploadError: string | null = null;
  docUploadError: string | null = null;

  // Propiedades para mensajes de éxito
  logoUploadSuccess: boolean = false;
  repPhotoUploadSuccess: boolean = false;
  docUploadSuccess: boolean = false;

  docReqIndex = 0;
  useRDMask = {
    primary: {
      code: '',
      enabled: false,
    },
    cel: {
      code: '',
      enabled: false,
    },
    secondary: {
      code: '',
      enabled: false,
    },
  };

  isLoading: boolean = false;

  isCountriesDropdownVisible = true;
  documentMask = '00000000000';
  documentLegalRepMask = '00000000000';
  maxDate = this.getMaxDate();

  readonly minAge: number = 18; // Edad mínima para registro
  wantToChangePassword: boolean = false; // Toggle for password change option

  // Variables para manejo local de archivos
  selectedLogoFile: File | null = null;
  selectedRepPhotoFile: File | null = null;
  selectedMercantileFile: File | null = null;
  selectedLegalRepIdFile: File | null = null;

  // Métodos y propiedad para gestión de direcciones (compatibles con el flujo anterior)
  openAddressDialog() {
    const dialogRef = this.dialog.open(AddressDialogComponent, {
      width: '70%',
      maxWidth: '100%',
      data: {
        addresses: this.addresses,
        mode: ViewModalEnum.Create,
        address: null,
        index: null,
      },
    });
    dialogRef.afterClosed().subscribe((result: AddAddress[] | null) => {
      if (result) {
        this.addresses = result;
        console.log(result);
      }
    });
  }

  editAddress(address: AddAddress, index: number) {
    const dialogRef = this.dialog.open(AddressDialogComponent, {
      width: '70%',
      maxWidth: '100%',
      data: {
        addresses: this.addresses,
        mode: ViewModalEnum.Edit,
        address: address,
        index: index,
      },
    });
    dialogRef.afterClosed().subscribe((result: AddAddress[] | null) => {
      if (result) {
        this.addresses = result;
      }
    });
  }

  viewAddress(address: AddAddress) {
    this.dialog.open(AddressDialogComponent, {
      width: '70%',
      maxWidth: '100%',
      data: {
        addresses: this.addresses,
        mode: ViewModalEnum.View,
        address: address,
        index: null,
      },
    });
  }

  removeAddress(i: number) {
    if (!this.addresses[i].id || this.addresses[i].id === 0) {
      this.addresses.splice(i, 1);
    } else {
      this.addresses[i].isEnabled = false;
    }
  }

  get visibleAddresses() {
    return this.addresses.filter((addr) => addr.isEnabled !== false);
  }

  constructor(
    private fb: FormBuilder,
    private dropdownService: DropdownService,
    private fileStorage: FileStorageService,
    private alert: AlertService,
    private auth: AuthService,
    private router: Router,
    private dialog: MatDialog,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadDropdowns();

    if ((this.isEditMode || this.isViewMode) && this.companyData) {
      this.form.patchValue({
        ...this.companyData,
        legalRepBirthDate: this.companyData.legalRepBirthDate
          ? this.datePipe.transform(
              this.companyData.legalRepBirthDate,
              'yyyy-MM-dd'
            )
          : null,
      });

      this.useRDMask.primary.code = this.companyData
        .legalRepPrimaryPhoneCountryCode as any;
      this.useRDMask.secondary.code = this.companyData
        .legalRepSecondaryPhoneCountryCode as any;
      this.useRDMask.cel.code = this.companyData
        .legalRepCellPhoneCountryCode as any;

      if (this.companyData.logoFileId) {
        this.logoPreview = this.fileStorage.getFileUrl(
          `${this.companyData.logoFileId}`
        );
      }
      if (this.companyData.legalRepPhotoFileId) {
        this.repPhotoPreview = this.fileStorage.getFileUrl(
          `${this.companyData.legalRepPhotoFileId}`
        );
      }
      if (this.companyData.mercantileRegistrationFileId) {
        this.mercantilePreview = this.fileStorage.getFileUrl(
          `${this.companyData.mercantileRegistrationFileId}`
        );
      }
      if (this.companyData.legalRepresentativeIDFileId) {
        this.legalRepIdPreview = this.fileStorage.getFileUrl(
          `${this.companyData.legalRepresentativeIDFileId}`
        );
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((this.isEditMode || this.isViewMode) && this.companyAddresses?.length) {
      this.addresses = this.companyAddresses.map((addr) => ({
        ...addr,
        isEnabled: addr.isEnabled,
      }));
    }
  }

  onCountryChange(event: any, type: any) {
    if (!event?.code) {
      return;
    }
    const isRD = event.code === 'DO';
    switch (type) {
      case 'primary':
        this.useRDMask.primary.enabled = isRD;
        break;
      case 'secondary':
        this.useRDMask.secondary.enabled = isRD;
        break;
      case 'cel':
        this.useRDMask.cel.enabled = isRD;
        break;
      default:
        this.useRDMask.primary.enabled = isRD;
        this.useRDMask.secondary.enabled = isRD;
        this.useRDMask.cel.enabled = isRD;
        break;
    }
  }

  buildForm() {
    //this.isViewMode = true
    // Si estamos en modo vista, no necesitamos validadores
    const validators = this.isViewMode
      ? []
      : [Validators.required, Validators.email];
    const requiredValidator = this.isViewMode ? [] : [Validators.required];
    const minAgeValidators = this.isViewMode
      ? []
      : [Validators.required, this.minAgeValidator(this.minAge)];
    const requiredTrueValidator =
      this.isViewMode || this.isEditMode ? [] : [Validators.requiredTrue];
    const passwordValidators =
      this.isViewMode || this.isEditMode ? [] : [passwordValidator];

    this.form = this.fb.group(
      {
        userId: [null],
        companyId: [null],
        email: ['', validators],
        website: [''],
        typeIdentificationId: ['', requiredValidator],
        identification: [''],
        razonSocial: ['', requiredValidator],
        tradeName: ['', requiredValidator],
        economicActivityId: ['', requiredValidator],
        directEmployees: [0, [Validators.min(0)]],
        indirectEmployees: [0, [Validators.min(0)]],
        companyDescription: [''],
        logoFileId: [null],
        mercantileRegistrationFileId: [null],
        legalRepresentativeIDFileId: [null],

        legalRepTypeIdentificationId: [''],
        legalRepIdentification: [''],
        legalRepFirstName: [''],
        legalRepLastName: [''],
        legalRepBirthDate: [null, minAgeValidators],
        legalRepPrimaryPhone: [''],
        primaryPhoneData: [''],
        legalRepSecondaryPhone: [''],
        secondaryPhoneData: [''],
        legalRepCellPhone: [''],
        cellPhoneData: [''],

        legalRepCellPhoneHasWhatsapp: [false],
        legalRepPhotoFileId: [null],

        password: ['', passwordValidators],
        confirmPassword: [''],
        wantToPublishOffers: [false],
        privacityTermsAccepted: [false, requiredTrueValidator],
        refundTermsAccepted: [false, requiredTrueValidator],
        changePassword: [false],
        hidePublicNameAndLogo: [false],
      },
      {
        validators:
          this.isViewMode || this.isEditMode
            ? []
            : [matchPasswords('password', 'confirmPassword')],
      }
    );

    // Si estamos en modo vista, deshabilitamos todos los controles
    if (this.isViewMode) {
      this.form.disable();
    }

    if (this.isEditMode) {
      this.form.get('changePassword')?.valueChanges.subscribe((value) => {
        this.wantToChangePassword = value;
        const passwordControl = this.form.get('password');
        const confirmPasswordControl = this.form.get('confirmPassword');

        if (value) {
          passwordControl?.setValidators([passwordValidator]);
          confirmPasswordControl?.setValidators([Validators.required]);
          // We need to set the form-level validator for password matching
          this.form.setValidators([
            matchPasswords('password', 'confirmPassword'),
          ]);
        } else {
          passwordControl?.clearValidators();
          confirmPasswordControl?.clearValidators();
          this.form.clearValidators();
        }

        passwordControl?.updateValueAndValidity();
        confirmPasswordControl?.updateValueAndValidity();
        this.form.updateValueAndValidity();
      });
    }
  }

  onDocumentLegalRepTypeChange(): void {
    const identificationtype = this.form.value.legalRepTypeIdentificationId;
    if (identificationtype) {
      const found = this.typeIdentList.find(
        (c) => c.id === +identificationtype
      );
      const relatedId = found?.relatedId ?? 0;
      // Si relatedId = 1, permitir alfanumérico (combinación de letras y números) sin máscara; si no, solo número
      this.documentLegalRepMask = relatedId === 1 ? '' : '00000000000';
    } else {
      this.documentLegalRepMask = '00000000000'; // Valor por defecto
    }
  }

  onDocumentTypeChange(): void {
    const identificationtype = this.form.value.typeIdentificationId;
    if (identificationtype) {
      const found = this.typeIdentList.find(
        (c) => c.id === +identificationtype
      );
      const relatedId = found?.relatedId ?? 0;
      // Si relatedId = 1, permitir alfanumérico (combinación de letras y números) sin máscara; si no, solo números
      this.documentMask = relatedId === 1 ? '' : '00000000000';
    } else {
      this.documentMask = '00000000000'; // Valor por defecto
    }
  }

  loadDropdowns() {
    this.dropdownService
      .GetOptions(DropDownKeywords.TypeIdentification)
      .subscribe((res) => {
        this.typeIdentList = res;
      });
    this.dropdownService
      .GetOptions(DropDownKeywords.EconomicActivity)
      .subscribe((res) => {
        this.economicActList = res;
      });
  }

  get legalRepAge(): string {
    const birth = this.form.get('legalRepBirthDate')?.value;
    if (!birth) return '';
    const bdate = new Date(birth);
    const diff = Date.now() - bdate.getTime();
    const age = new Date(diff).getUTCFullYear() - 1970;
    return age > 0 ? age.toString() : '';
  }

  get birthDate(): AbstractControl {
    return this.form.get('legalRepBirthDate')!;
  }

  get isSubmitButtonDisabled(): boolean {
    return (this.form.invalid && this.form.touched) || this.isLoading;
  }

  // Password validation helper methods
  hasUppercaseError(): boolean {
    const password = this.form.get('password')?.value;
    return !password || !/[A-Z]/.test(password);
  }

  hasLowercaseError(): boolean {
    const password = this.form.get('password')?.value;
    return !password || !/[a-z]/.test(password);
  }

  hasDigitError(): boolean {
    const password = this.form.get('password')?.value;
    return !password || !/[0-9]/.test(password);
  }

  getMaxDate(): string {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 18);
    return today.toISOString().split('T')[0]; // formato yyyy-MM-dd
  }

  minAgeValidator(minAge: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      const birthDate = new Date(value);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      const d = today.getDate() - birthDate.getDate();

      const isUnderage =
        age < minAge || (age === minAge && (m < 0 || (m === 0 && d < 0)));
      return isUnderage ? { underage: true } : null;
    };
  }

  onLogoClick() {
    document.getElementById('fileLogo')?.click();
  }

  onLogoSelected(event: any) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      input.value = '';
      return;
    }

    if (!this.validatePhotoFileType(file)) {
      this.alert.showWarningAlert(
        'Archivo inválido',
        'Solo se permiten imágenes JPG o PNG para el logo'
      );
      input.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      this.alert.showWarningAlert(
        'Archivo inválido',
        `El archivo excede el tamaño máximo permitido (${(
          MAX_FILE_SIZE /
          1024 /
          1024
        ).toFixed(0)} MB)`
      );
      input.value = '';
      return;
    }

    this.selectedLogoFile = file;
    this.logoUploadError = null;
    this.logoUploadSuccess = false;
    this.previewFile(file, (preview) => {
      this.logoPreview = preview;
    });
    // No subir aquí, solo guardar localmente
    input.value = '';
  }

  removeLogo() {
    this.selectedLogoFile = null;
    this.logoPreview = null;
    this.form.patchValue({ logoFileId: null });
  }

  onRepPhotoClick() {
    document.getElementById('fileFoto')?.click();
  }

  onRepPhotoSelected(event: any) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      input.value = '';
      return;
    }

    if (!this.validatePhotoFileType(file)) {
      this.alert.showWarningAlert(
        'Archivo inválido',
        'Solo se permiten imágenes JPG o PNG para la foto del representante'
      );
      input.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      this.alert.showWarningAlert(
        'Archivo inválido',
        `El archivo excede el tamaño máximo permitido (${(
          MAX_FILE_SIZE /
          1024 /
          1024
        ).toFixed(0)} MB)`
      );
      input.value = '';
      return;
    }

    this.selectedRepPhotoFile = file;
    this.repPhotoUploadError = null;
    this.repPhotoUploadSuccess = false;
    this.previewFile(file, (preview) => {
      this.repPhotoPreview = preview;
    });
    // No subir aquí, solo guardar localmente
    input.value = '';
  }

  removeRepPhoto() {
    this.selectedRepPhotoFile = null;
    this.repPhotoPreview = null;
    this.form.patchValue({ legalRepPhotoFileId: null });
  }

  onDocReqClick(index: number) {
    this.docReqIndex = index;
    document.getElementById('fileDocReq')?.click();
  }

  onDocReqSelected(event: any) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      input.value = '';
      return;
    }

    if (!this.validateFileType(file)) {
      this.alert.showWarningAlert(
        'Archivo inválido',
        'Solo se permiten imágenes o PDF'
      );
      input.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      this.alert.showWarningAlert(
        'Archivo inválido',
        `El archivo excede el tamaño máximo permitido (${(
          MAX_FILE_SIZE /
          1024 /
          1024
        ).toFixed(0)} MB)`
      );
      input.value = '';
      return;
    }

    this.docUploadError = null;
    this.docUploadSuccess = false;

    if (this.docReqIndex === 0) {
      this.selectedMercantileFile = file;
      this.previewFile(file, (preview) => {
        this.mercantilePreview = preview;
      });
    } else {
      this.selectedLegalRepIdFile = file;
      this.previewFile(file, (preview) => {
        this.legalRepIdPreview = preview;
      });
    }
    // No subir aquí, solo guardar localmente
    input.value = '';
  }

  removeMercantile() {
    this.selectedMercantileFile = null;
    this.mercantilePreview = null;
    this.form.patchValue({ mercantileRegistrationFileId: null });
  }

  removeLegalRepId() {
    this.selectedLegalRepIdFile = null;
    this.legalRepIdPreview = null;
    this.form.patchValue({ legalRepresentativeIDFileId: null });
  }

  async onSubmit() {
    if (this.form.invalid) {
      const missingFields = this.getMissingRequiredFields(this.form);
      this.alert.showWarningAlert('Verifique los campos del formulario');
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.value;

    if (!value.identification || value.identification?.length < 6) {
      this.alert.showWarningAlert(
        'Verifique el número de identificación de la empresa',
        'El número de identificación no es válido'
      );
      return;
    }

    if (
      !value.legalRepIdentification ||
      value.legalRepIdentification?.length < 6
    ) {
      this.alert.showWarningAlert(
        'Verifique el número de identificación del representante legal',
        'El número de identificación no es válido'
      );
      return;
    }

    // Subir archivos SOLO si hay archivos seleccionados localmente
    this.isLoading = true;
    try {
      let logoFileId = value.logoFileId;
      let repPhotoFileId = value.legalRepPhotoFileId;
      let mercantileFileId = value.mercantileRegistrationFileId;
      let legalRepIdFileId = value.legalRepresentativeIDFileId;

      if (this.selectedLogoFile) {
        const res = await this.fileStorage
          .uploadFile(this.selectedLogoFile)
          .toPromise();
        if (res && res.fileId) {
          logoFileId = res.fileId;
          this.form.patchValue({ logoFileId });
        }
      }
      if (this.selectedRepPhotoFile) {
        const res = await this.fileStorage
          .uploadFile(this.selectedRepPhotoFile)
          .toPromise();
        if (res && res.fileId) {
          repPhotoFileId = res.fileId;
          this.form.patchValue({ legalRepPhotoFileId: repPhotoFileId });
        }
      }
      if (this.selectedMercantileFile) {
        const res = await this.fileStorage
          .uploadFile(this.selectedMercantileFile)
          .toPromise();
        if (res && res.fileId) {
          mercantileFileId = res.fileId;
          this.form.patchValue({
            mercantileRegistrationFileId: mercantileFileId,
          });
        }
      }
      if (this.selectedLegalRepIdFile) {
        const res = await this.fileStorage
          .uploadFile(this.selectedLegalRepIdFile)
          .toPromise();
        if (res && res.fileId) {
          legalRepIdFileId = res.fileId;
          this.form.patchValue({
            legalRepresentativeIDFileId: legalRepIdFileId,
          });
        }
      }

      // Verificar que se hayan subido los documentos obligatorios
      if (!this.isViewMode) {
        if (!mercantileFileId) {
          this.alert.showWarningAlert(
            'Documentos requeridos',
            'Debe subir el registro mercantil para continuar'
          );
          this.isLoading = false;
          return;
        }
        if (!legalRepIdFileId) {
          this.alert.showWarningAlert(
            'Documentos requeridos',
            'Debe subir la cédula del representante legal para continuar'
          );
          this.isLoading = false;
          return;
        }
      }

      const request: RegisterRequest = {
        userId: +value.userId || 0,
        userName: value.email,
        firstName: value.razonSocial,
        lastName: '',
        email: value.email,
        typeIdentificationId: +value.typeIdentificationId,
        identification: value.identification,
        roleId: UserRoleIds.Company,
        phoneNumber: '',
        password: value.password,
        changePassword: value.changePassword,
        privacityTermsAccepted: value.privacityTermsAccepted,
        refundTermsAccepted: value.refundTermsAccepted,
        extendedData: {
          companyId: +value.companyId || 0,
          website: value.website,
          razonSocial: value.razonSocial,
          tradeName: value.tradeName,
          economicActivityId: +value.economicActivityId,
          directEmployees: +value.directEmployees,
          indirectEmployees: +value.indirectEmployees,
          companyDescription: value.companyDescription,
          logoFileId: logoFileId,
          mercantileRegistrationFileId: mercantileFileId,
          legalRepresentativeIDFileId: legalRepIdFileId,
          legalRepTypeIdentificationId:
            +value.legalRepTypeIdentificationId || null,
          legalRepIdentification: value.legalRepIdentification,
          legalRepFirstName: value.legalRepFirstName,
          legalRepLastName: value.legalRepLastName,
          legalRepBirthDate: value.legalRepBirthDate,
          legalRepPrimaryPhone: value.legalRepPrimaryPhone,
          legalRepPrimaryPhoneCountryISO2: value.primaryPhoneData.isoCode,
          legalRepPrimaryPhoneCountryDialCode:
            value.primaryPhoneData.dialling_code,
          legalRepPrimaryPhoneCountryCode: value.primaryPhoneData.code,
          legalRepPrimaryPhoneE164:
            value.primaryPhoneData.dialling_code + value.legalRepPrimaryPhone,
          legalRepSecondaryPhone: value.legalRepSecondaryPhone,
          legalRepSecondaryPhoneCountryISO2: value.secondaryPhoneData.isoCode,
          legalRepSecondaryPhoneCountryDialCode:
            value.secondaryPhoneData.dialling_code,
          legalRepSecondaryPhoneCountryCode: value.secondaryPhoneData.code,
          legalRepSecondaryPhoneE164:
            value.secondaryPhoneData.dialling_code +
            value.legalRepSecondaryPhone,
          legalRepCellPhone: value.legalRepCellPhone,
          legalRepCellPhoneCountryISO2: value.cellPhoneData.isoCode,
          legalRepCellPhoneCountryDialCode: value.cellPhoneData.dialling_code,
          legalRepCellPhoneCountryCode: value.cellPhoneData.code,
          legalRepCellPhoneE164:
            value.cellPhoneData.dialling_code + value.legalRepCellPhone,
          legalRepCellPhoneHasWhatsapp: value.legalRepCellPhoneHasWhatsapp,
          legalRepPhotoFileId: repPhotoFileId,
          wantToPublishOffers: value.wantToPublishOffers,
          hidePublicNameAndLogo: value.hidePublicNameAndLogo,
        },
        addresses: [...this.addresses],
      };
      if (!this.isEditMode) {
        this.auth
          .register(request)
          .subscribe({
            next: () => {
              this.alert.showSuccessAlert('Registro exitoso');
              this.savedEmitter.emit(true);
              this.router.navigate(['/login']);
            },
            error: (err) => {
              if (err) {
                this.alert.showErrorAlert(err[0]);
              } else {
                this.alert.showErrorAlert('Error al registrar');
              }
            },
          })
          .add(() => {
            this.isLoading = false;
          });
      } else {
        this.auth
          .updateAccount(request)
          .subscribe({
            next: () => {
              this.alert.showSuccessAlert('Actualización exitosa');
              this.savedEmitter.emit(true);
            },
            error: (err) => {
              if (err) {
                this.alert.showErrorAlert(err[0]);
              } else {
                this.alert.showErrorAlert('Error al actualizar');
              }
            },
          })
          .add(() => {
            this.isLoading = false;
          });
      }
    } catch (err) {
      this.isLoading = false;
      this.alert.showErrorAlert('Error al subir archivos. Intente nuevamente.');
      console.error('Error al subir archivos:', err);
    }
  }

  getMissingRequiredFields(form: FormGroup): string[] {
    const missingFields: string[] = [];

    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);

      if (control && control.invalid && control.errors?.['required']) {
        missingFields.push(key); // Store the name of the missing required field
      }
    });

    return missingFields;
  }
}
